import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Music, Video, Globe, Award, TrendingUp, User, ChevronLeft } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 1,
      title: 'STUDIO MUSICAL',
      icon: Music,
      description: 'Production musicale, enregistrement, mixage, production vidéo, clips et émissions chrétiennes.',
      action: 'RÉSERVEZ VOTRE PLACE',
      link: '/studio-musical',
      color: 'orange'
    },
    {
      id: 2,
      title: 'VENTE DE VIDÉOS ET AUDIOS',
      icon: Video,
      description: 'Permettez à vos fans d\'acheter directement vos films, séries, clips et musiques sur AGRP BOX. Paiement par Orange Money, Airtel Money, Visa et Mastercard.',
      color: 'blue'
    },
    {
      id: 3,
      title: 'DISTRIBUTION NUMÉRIQUE',
      icon: Globe,
      description: 'Nous mettons votre musique et vos vidéos sur Spotify, Boomplay, YouTube Music, Apple Music et 50+ autres plateformes mondiales.',
      color: 'green'
    },
    {
      id: 4,
      title: 'CRÉATION DE CHAÎNE ARTISTE OFFICIELLE',
      icon: Award,
      description: 'Chaque artiste dispose de sa propre chaîne officielle sur AGRP BOX pour centraliser toutes ses œuvres et développer sa fanbase. (Et aussi création chaîne officielle Vevo)',
      color: 'purple'
    },
    {
      id: 5,
      title: 'BOOSTAGE ET PROMOTION',
      icon: TrendingUp,
      description: 'Nous boostons vos chansons, clips et événements sur Facebook, TikTok, YouTube et Instagram pour toucher plus d\'âmes.',
      color: 'red'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Bouton de retour - Comme Studio Musical */}
      <div className="border-b border-gray-200 bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#E50914] transition-colors font-semibold"
          >
            <ChevronLeft size={20} />
            <span>Retour</span>
          </button>
          <h1 className="text-2xl font-bold text-[#E50914]" style={{ fontFamily: 'Outfit' }}>
            À PROPOS
          </h1>
          <div className="w-24"></div> {/* Spacer pour centrer le titre */}
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#E50914] to-orange-600 text-white py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="/logo.png" 
            alt="AGRP BOX Logo" 
            className="h-24 w-auto mx-auto mb-6 drop-shadow-2xl"
          />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4" style={{ fontFamily: 'Outfit' }}>
            AGRP BOX
          </h1>
          <p className="text-2xl sm:text-3xl font-bold mb-2">
            Première Plateforme Numérique Chrétienne en RDC
          </p>
          <p className="text-lg text-white/90">
            www.agrpbox.com
          </p>
        </div>
      </div>

      {/* Qui sommes-nous */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">
          🙏 Qui sommes-nous ?
        </h2>
        <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-8 shadow-lg">
          <p className="text-gray-800 text-lg leading-relaxed mb-6">
            <strong className="text-[#E50914]">AGRP BOX</strong> est la première plateforme numérique chrétienne en <strong>République Démocratique du Congo</strong>, dédiée à la création, à la production, à la diffusion et à la monétisation des contenus audiovisuels et musicaux.
          </p>
          <p className="text-gray-800 text-lg leading-relaxed mb-6">
            Accessible sur <a href="http://www.agrpbox.com" className="text-[#E50914] font-bold underline">www.agrpbox.com</a>, notre mission est d'accompagner les artistes, créateurs de contenus, producteurs et serviteurs de Dieu dans la réalisation et la diffusion de leurs projets.
          </p>
          
          {/* Fondateur */}
          <Card className="bg-white border-2 border-[#E50914] shadow-xl mt-8">
            <CardHeader className="bg-gradient-to-r from-[#E50914] to-orange-600 text-white">
              <CardTitle className="flex items-center text-2xl">
                <User size={32} className="mr-3" />
                Fondateur
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-2xl font-black text-gray-900 text-center">
                M. Raoul Mavuna Ma Lelo
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Nos 5 Services */}
      <div className="bg-gray-100 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-gray-900 text-center">
            🎯 Nos 5 Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={service.id} 
                  className="bg-white border-2 border-gray-300 shadow-lg hover:shadow-2xl transition-all hover:scale-105"
                >
                  <CardHeader className="bg-gray-50 border-b-2 border-gray-200">
                    <CardTitle className="flex items-center text-lg text-gray-900">
                      <div className={`p-3 rounded-full bg-${service.color}-100 mr-3`}>
                        <Icon size={28} className={`text-${service.color}-600`} />
                      </div>
                      <span className="font-bold">{service.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">
                      {service.description}
                    </p>
                    
                    {service.action && (
                      <Button
                        onClick={() => navigate(service.link)}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3"
                      >
                        {service.action}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notre Vision */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">
          🌟 Notre Vision
        </h2>
        <Card className="bg-gradient-to-r from-[#E50914] to-orange-600 border-none shadow-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-white text-xl sm:text-2xl font-bold leading-relaxed">
              Contribuer au développement de l'industrie musicale chrétienne en Afrique en mettant le numérique au service de l'Évangile.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* CTA Final */}
      <div className="bg-gray-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-6">
            Rejoignez-nous dès aujourd'hui ! 🎵
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            Faites partie de la première plateforme numérique chrétienne en RDC
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-[#E50914] hover:bg-[#F8212C] text-white font-bold text-lg py-6 px-12"
            >
              S'inscrire maintenant
            </Button>
            <Button
              onClick={() => navigate('/studio-musical')}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg py-6 px-12"
            >
              Découvrir le Studio
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
